/**
 * SoldierCard component for displaying a single soldier.
 * Stats/cost are read-only (from template); only name/notes/tags are editable.
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Badge,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui';
import { Trash2, Edit2, Shield, Swords, Target, Footprints, Brain, Heart } from 'lucide-react';
import { cn } from '@/utils';
import type { Soldier, SoldierTemplate, SoldierMetaPatch } from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SoldierCardProps {
  soldier: Soldier;
  template: SoldierTemplate;
  onUpdate: (id: string, patch: SoldierMetaPatch) => void;
  onRemove: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Stat Display Component
// ---------------------------------------------------------------------------

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  className?: string;
}

function Stat({ icon, label, value, className }: StatProps) {
  return (
    <div className={cn('flex flex-col items-center gap-0.5', className)}>
      <div className="flex items-center gap-1 text-muted-foreground">
        {icon}
        <span className="text-[10px] uppercase">{label}</span>
      </div>
      <span className="font-mono text-sm font-semibold">
        {typeof value === 'number' && value > 0 ? `+${value}` : value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SoldierCard({ soldier, template, onUpdate, onRemove }: SoldierCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(soldier.name || '');
  const [editNotes, setEditNotes] = useState(soldier.notes || '');

  const displayName = soldier.name || template.name;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSaveEdit = () => {
    onUpdate(soldier.id, {
      name: editName.trim() || undefined,
      notes: editNotes.trim() || undefined,
    });
    setIsEditing(false);
  };

  const handleOpenEdit = () => {
    setEditName(soldier.name || '');
    setEditNotes(soldier.notes || '');
    setIsEditing(true);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card className="w-full">
      <CardContent className="p-3">
        {/* Header Row */}
        <div className="mb-2 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{displayName}</h4>
              {template.isSpecialist && (
                <Badge variant="secondary" className="text-[10px]">
                  Specialist
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{template.name}</p>
          </div>

          <div className="flex items-center gap-1">
            <Badge variant="outline" className="font-mono">
              {template.cost}gc
            </Badge>

            <Sheet open={isEditing} onOpenChange={setIsEditing}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleOpenEdit}
                  aria-label={`Edit ${displayName}`}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Edit {template.name}</SheetTitle>
                </SheetHeader>
                <div className="space-y-4 p-4">
                  <div className="space-y-2">
                    <label htmlFor="edit-name" className="text-sm font-medium">
                      Custom Name
                    </label>
                    <Input
                      id="edit-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={template.name}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="edit-notes" className="text-sm font-medium">
                      Notes
                    </label>
                    <textarea
                      id="edit-notes"
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add notes..."
                      rows={3}
                      className={cn(
                        'flex w-full rounded-md border border-input bg-background px-3 py-2',
                        'text-sm placeholder:text-muted-foreground',
                        'focus-visible:outline-none focus-visible:ring-2',
                        'focus-visible:ring-ring focus-visible:ring-offset-2'
                      )}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Stats and cost cannot be edited — they are fixed per template.
                  </p>
                  <Button onClick={handleSaveEdit} className="w-full">
                    Save Changes
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onRemove(soldier.id)}
              aria-label={`Remove ${displayName}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-6 gap-1 rounded-md bg-muted/50 p-2">
          <Stat
            icon={<Footprints className="h-3 w-3" />}
            label="M"
            value={template.move}
          />
          <Stat
            icon={<Swords className="h-3 w-3" />}
            label="F"
            value={template.fight}
          />
          <Stat
            icon={<Target className="h-3 w-3" />}
            label="S"
            value={template.shoot}
          />
          <Stat
            icon={<Shield className="h-3 w-3" />}
            label="A"
            value={template.armor}
          />
          <Stat
            icon={<Brain className="h-3 w-3" />}
            label="W"
            value={template.will}
          />
          <Stat
            icon={<Heart className="h-3 w-3" />}
            label="H"
            value={template.health}
          />
        </div>

        {/* Notes */}
        {soldier.notes && (
          <p className="mt-2 text-xs text-muted-foreground">{soldier.notes}</p>
        )}

        {/* Tags */}
        {soldier.tags && soldier.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {soldier.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px]">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

